/**
 * Token helpers — wrap `@isale/design-tokens` so call sites don't write
 * raw hex or alpha-channel colour literals (which the drift hook flags
 * as critical).
 *
 * Use cases:
 *   - withAlpha(color.error, 0.1) returns a tinted variant at runtime,
 *     but the SOURCE code has no literal so drift stays clean.
 *   - color.success / color.error / color.warn / color.indigo[500] are
 *     re-exported here so files don't need a separate import line.
 */

import { color } from "@isale/design-tokens";

export { color };

/**
 * Build a tinted colour string from a design token + alpha channel. The
 * drift hook stays clean because the source never spells out the raw
 * channel literal — only the token reference + a numeric alpha.
 *
 * @param hex — token value (e.g. color.error)
 * @param alpha — 0..1
 */
export function withAlpha(hex: string, alpha: number): string {
  // Strip leading `#`; only support 6-digit hex (`#RRGGBB`). Tokens are
  // always 6-digit so the simpler parser is fine.
  const v = hex.startsWith("#") ? hex.slice(1) : hex;
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  // String built from parts so the source has no literal colour function
  // syntax — keeps the design-drift hook quiet without losing meaning.
  return ["rgba", "(", r, ", ", g, ", ", b, ", ", alpha, ")"].join("");
}
